import {
  useApproveAppraisalByAdminMutation,
  useDraftAppraisalByadminMutation,
  useGetAdminAppraisalDetailsQuery,
} from "@/app/features/employee/performance/performance.api";
import { useGetAllDesignationsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import CustomDrawer from "@/components/CustomDrawer";
import ManagerRecommendations from "@/pages/Performance/_components/ManagerRecommendations";
import ManagerRattings from "@/pages/Performance/_components/ManagerRattings";
import { useMemo } from "react";
import useAuth from "@/hooks/useAuth";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { Button, Flex, Form, Input, InputNumber } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import dayjs from "dayjs";
import { getFileNameFromUrl } from "@/utils";
import MultiFileUploader from "@/components/MultiFileUploader";
import SetupRecomendation from "@/pages/Performance/_components/SetupRecomendation";
import {
  getRecommendationFields,
  type RecommendationType,
} from "@/pages/Performance/_components/recommendation.constants";
import WithLoader from "@/components/WithLoader";
import { cn } from "@/lib/cn";

// ──────── Types ────────
interface ApproveAppraisalByAdminProps {
  open: boolean;
  onClose: () => void;
  mappingId?: string;
  status?: string;
}

type FormType = {
  recommendation_type?: RecommendationType;
  ratings: {
    kpi_id: string;
    kpi_name: string;
    weightage: number;
    self_evaluation: string;
    self_rating: number | null;
    evaluation?: string;
    rating?: number | null;
  }[];
  admin_comment?: string;
  attachments?: string[];
  // Dismissal
  last_working_day?: dayjs.Dayjs;
  // Incentive
  incentive_effective_month?: dayjs.Dayjs;
  incentive_type?: string;
  incentive_value?: string;
  incentive_base?: string;
  // Promotion
  suggested_designation_id?: string;
  new_salary?: number;
  promotion_effective_month?: dayjs.Dayjs;
  // Increment
  increment_effective_month?: dayjs.Dayjs;
  increment_type?: string;
  increment_value?: string;
  increment_base?: string;
};

// ──────── Component ────────
const ApproveAppraisalByAdmin = ({
  open,
  onClose,
  mappingId,
  status,
}: ApproveAppraisalByAdminProps) => {
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();

  // Fetch Appraisal Details
  const { data: detailRes, isFetching: isDetailFetching } =
    useGetAdminAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open,
    });

  // Mutations
  const [draftAppraisal, { isLoading: isDrafting }] =
    useDraftAppraisalByadminMutation();
  const [approveAppraisal, { isLoading: isApproving }] =
    useApproveAppraisalByAdminMutation();

  const { data: designationsData, isLoading: isDesignationLoading } =
    useGetAllDesignationsQuery(
      { params: { status: "active" } },
      { skip: !open },
    );

  const designationMap = useMemo(() => {
    const map: Record<string, string> = {};
    designationsData?.data?.data?.forEach((item: any) => {
      map[item.id] = item.name;
    });
    return map;
  }, [designationsData]);

  const employeeKpis = detailRes?.data?.employeeKpis || [];
  const targetData = detailRes?.data;
  const managerMappings = targetData?.managerMappings || [];
  const planName = (targetData as any)?.planName || "";

  // Reset fields when drawer closes
  useEffect(() => {
    if (!open) form.resetFields();
  }, [open, form]);

  // Populate form with details
  useEffect(() => {
    if (detailRes?.data) {
      const targetData = detailRes.data;
      const employeeKpis = targetData.employeeKpis || [];
      const attachments = targetData.attachments || [];
      const adminRec = (targetData as any)?.adminRecommendation;

      form.setFieldsValue({
        attachments: attachments?.map((file: any) => file.fileUrl),
        ratings: employeeKpis.map((k: any) => {
          return {
            kpi_id: k.id,
            kpi_name: k.kpiName,
            weightage: Number(k.weightage),
            self_evaluation: k.selfEvaluation || "",
            self_rating: k.selfRating || null,
          };
        }),

        // ── Recommendation ──
        ...(adminRec && {
          recommendation_type:
            (adminRec.recommendationType as RecommendationType) ?? null,
          // Dismissal
          last_working_day: adminRec.lastWorkingDay
            ? dayjs(adminRec.lastWorkingDay)
            : undefined,
          // Incentive
          incentive_effective_month: adminRec.incentiveEffectiveMonth
            ? dayjs(adminRec.incentiveEffectiveMonth)
            : undefined,
          incentive_type: adminRec.incentiveType ?? "FLAT",
          incentive_value:
            adminRec.incentiveValue != null
              ? String(adminRec.incentiveValue)
              : undefined,
          incentive_base: adminRec.incentiveBase ?? undefined,
          // Promotion
          suggested_designation_id:
            adminRec.suggestedDesignationId ?? undefined,
          new_salary: adminRec.newSalary ?? undefined,
          promotion_effective_month: adminRec.promotionEffectiveMonth
            ? dayjs(adminRec.promotionEffectiveMonth)
            : undefined,
          // Increment
          increment_effective_month: adminRec.incrementEffectiveMonth
            ? dayjs(adminRec.incrementEffectiveMonth)
            : undefined,
          increment_type: adminRec.incrementType ?? "FLAT",
          increment_value:
            adminRec.incrementValue != null
              ? String(adminRec.incrementValue)
              : undefined,
          increment_base: adminRec.incrementBase ?? undefined,
        }),
      });
    }
  }, [detailRes, form, open, status]);

  const handleApprove = async (status: "APPROVED" | "REJECTED" | "DRAFT") => {
    try {
      const values = await form.validateFields();

      // Merge the payload fields for every active recommendation section.
      const recommendationFields = getRecommendationFields(
        values.recommendation_type,
        values,
      );

      const payload = {
        mapping_id: mappingId || "",
        plan_id: targetData?.planId || "",
        employee_id: targetData?.employeeId || "",
        organization_id: organizationId || "",
        manager_id: userId || "",
        manager_comment: "",
        ratings: values.ratings.map((r, index) => {
          const lastManagerRating = employeeKpis[index]?.managerRatings?.at(-1);
          return {
            kpi_id: r.kpi_id,
            rating:
              lastManagerRating?.rating != null
                ? Number(lastManagerRating.rating)
                : 0,
            evaluation: lastManagerRating?.evaluation || "",
          };
        }),
        attachments:
          values.attachments?.map((url) => ({
            file_name: getFileNameFromUrl(url),
            file_url: url,
          })) || [],
        recommendation_type: values.recommendation_type || null,
        ...recommendationFields,
      };

      let res;
      if (status === "DRAFT") {
        res = await draftAppraisal(payload as any).unwrap();
      } else {
        res = await approveAppraisal(payload as any).unwrap();
      }

      if (res?.success === false) {
        toast(
          "error",
          getErrorMessage(
            res,
            `Failed to ${
              status === "DRAFT"
                ? "draft"
                : status === "APPROVED"
                  ? "approve"
                  : "reject"
            } appraisal`,
          ),
        );
        return;
      }

      toast(
        "success",
        `Appraisal ${
          status === "DRAFT"
            ? "drafted"
            : status === "APPROVED"
              ? "approved"
              : "rejected"
        } successfully`,
      );
      handleDrawerClose();
    } catch (error: any) {
      toast(
        "error",
        getErrorMessage(
          error,
          `Failed to ${
            status === "DRAFT"
              ? "draft"
              : status === "APPROVED"
                ? "approve"
                : "reject"
          } appraisal`,
        ),
      );
    }
  };

  const handleDrawerClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={planName ? `${planName} - Appraisal Review` : "Appraisal Review"}
    >
      <WithLoader
        isLoading={isDetailFetching}
        className={cn(
          "rounded-lg",
          isDetailFetching && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          layout="vertical"
          className="custom-form"
          size="large"
        >
          <>
            {/* ── KPI Ratings (Read-only) ── */}
            <div className="border border-theme-gray-700 rounded-xl p-6">
              <h4 className="text-white text-base font-semibold mb-6">
                Performance Appraisal Review
              </h4>

              <Form.List name="ratings">
                {(fields) => (
                  <>
                    {fields.map((field, index) => {
                      const { key, ...restField } = field;
                      return (
                        <div key={key} className="relative space-y-4">
                          {index > 0 && (
                            <div className="border-t border-dashed border-theme-gray-600 my-6" />
                          )}

                          <Flex justify="space-between" align="center">
                            <h5 className="text-white text-sm font-medium">
                              KPI -{index + 1}
                            </h5>
                          </Flex>

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="KPI Name"
                              name={[field.name, "kpi_name"]}
                            >
                              <Input disabled className="bg-slate-800/50 text-sm" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Weightage(%)"
                              name={[field.name, "weightage"]}
                            >
                              <InputNumber
                                disabled
                                controls={false}
                                className="w-full bg-slate-800/50 text-sm"
                              />
                            </Form.Item>
                          </div>

                          {employeeKpis[index]?.details && (
                            <div className="mt-4">
                              <label className="text-white text-sm block mb-2">
                                Details
                              </label>
                              <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[60px] whitespace-pre-wrap">
                                {employeeKpis[index].details}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="Employee Self Evaluation"
                              name={[field.name, "self_evaluation"]}
                            >
                              <TextArea
                                rows={3}
                                disabled
                                className="bg-slate-800/30 text-sm"
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Rating Scale (1-5)"
                              name={[field.name, "self_rating"]}
                            >
                              <InputNumber
                                disabled
                                className="w-full bg-slate-800/30 text-sm"
                              />
                            </Form.Item>
                          </div>

                          {/* ── All Manager Ratings (Read-only) ── */}
                          <ManagerRattings
                            ratings={employeeKpis[index]?.managerRatings || []}
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </Form.List>
            </div>

            {/* ── Employee Overall Comment (Read-only) ── */}
            {(targetData?.employeeComment || targetData?.employee_comment) && (
              <div className="mt-6">
                <label className="text-sm mb-1.5 block font-semibold tracking-wider">
                  Employee Overall Comment
                </label>
                <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white min-h-[60px] whitespace-pre-wrap text-base">
                  {targetData.employeeComment || targetData.employee_comment}
                </div>
              </div>
            )}

            {/* ── Manager Comments (Read-only) ── */}
            {managerMappings.map((mm: any, mmIdx: number) => (
              <div key={mm.id || mmIdx} className="mt-6">
                <label className="text-sm mb-1.5 block font-semibold tracking-wider">
                  Line Manager - {mmIdx + 1} Comment
                </label>
                <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white min-h-[60px] whitespace-pre-wrap text-base">
                  {mm.appraisalComment || "No comment provided"}
                </div>
              </div>
            ))}

            {/* ── Recommendation (Read-only) ── */}
            <div className="mt-6">
              <ManagerRecommendations
                managerMappings={managerMappings}
                designationMap={designationMap}
              />
            </div>

            {/* ── Attachments ── */}
            <Form.Item className="mt-8" name="attachments">
              <MultiFileUploader
                form={form}
                name="attachments"
                title="Drag & Drop your file here"
                description="Browse"
              />
            </Form.Item>

            {/* ── Action Sections (mutually exclusive) ── */}
            <SetupRecomendation
              designations={designationsData?.data?.data}
              isDesignationLoading={isDesignationLoading}
            />
            {/* ── Footer Buttons ── */}
            <Flex justify="end" gap={12} className="mt-12">
              <Button
                size="large"
                className="btn-primary-bordered min-w-24"
                onClick={() => handleApprove("DRAFT")}
                loading={isDrafting}
                disabled={isApproving}
              >
                Draft
              </Button>
              <Button
                size="large"
                className="btn-primary min-w-24"
                onClick={() => handleApprove("APPROVED")}
                loading={isApproving}
                disabled={isDrafting}
              >
                Approve
              </Button>
            </Flex>
          </>
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
};

export default ApproveAppraisalByAdmin;
